/**
 * Cloudinary URL transformation helper.
 *
 * Inserts on-the-fly transformations (resize, quality, format) into a Cloudinary
 * `secure_url`. Non-Cloudinary URLs (Unsplash, Pexels, /api/uploads/...) are returned
 * unchanged so callers can use this everywhere safely.
 *
 * Cloudinary URL pattern:
 *   https://res.cloudinary.com/<cloud>/image/upload/<transformations>/<public_id>.<ext>
 *
 * Usage:
 *   <img src={cld(product.image, 'w_400,h_400,c_fill,q_auto,f_auto')} />
 */

const CLOUDINARY_HOST = 'res.cloudinary.com';
// Default transformations applied to every Cloudinary image — auto-quality + auto-format
// (browser-optimal: WebP/AVIF where supported)
const BASE = 'q_auto,f_auto';

// Watermark overlay as its own transformation step. In Cloudinary URL syntax each
// transformation step is separated by `/`. We compose steps using cldChain() below.
// l_brainwave:brand:logo = use /brainwave/brand/logo asset (slash -> colon in overlay names)
// w_0.15,fl_relative = 15% of underlying image width; g_south_east = anchor bottom-right; o_55 = 55% opacity
const WATERMARK_STEP = 'l_brainwave:brand:logo,w_0.15,fl_relative,g_south_east,x_20,y_20,o_55';

const cldChain = (url, steps) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes(CLOUDINARY_HOST)) return url;
  const parts = url.split('/upload/');
  if (parts.length !== 2) return url;
  const joined = steps.filter(Boolean).join('/');
  return `${parts[0]}/upload/${joined}/${parts[1]}`;
};

// Single-step helper (kept for backwards compatibility with existing callers)
export const cld = (url, transformations = '') => {
  if (!transformations) return cldChain(url, [BASE]);
  return cldChain(url, [`${transformations},${BASE}`]);
};

// Curated presets used across the app — keep callers concise & consistent.
// `g_auto:subject` uses Cloudinary's AI to detect the main subject and keep it in frame when cropping.
export const cldPresets = {
  // Card thumbnails — AI-cropped to keep the product/subject centered
  productCard: (u) => cldChain(u, ['w_600,h_600,c_fill,g_auto:subject', BASE]),
  cartItem:    (u) => cldChain(u, ['w_200,h_200,c_fill,g_auto:subject', BASE]),
  homeFeature: (u) => cldChain(u, ['w_800,h_600,c_fill,g_auto:subject', BASE]),
  serviceCard: (u) => cldChain(u, ['w_800,h_500,c_fill,g_auto:subject', BASE]),
  adminThumb:  (u) => cldChain(u, ['w_300,h_300,c_fill,g_auto:subject', BASE]),

  // Gallery: preserve native aspect ratio for masonry, AI-aware quality + Brainwave watermark (own step)
  galleryCard:     (u) => cldChain(u, ['w_900,c_limit', WATERMARK_STEP, BASE]),
  galleryLightbox: (u) => cldChain(u, ['w_1800,c_limit', WATERMARK_STEP, BASE]),
};

export default cld;
